import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Alert, Button, Card, CardContent, CardHeader, CircularProgress, Divider, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DateTime } from "luxon";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { fetchLatestPermanentBid, fetchPermanentBidPDF, deletePermanentBidEvent } from "../api/bid";
import { isUserAuthorizedByServer } from "../api/userAccess";
import { getPresignedUrl, uploadFileToS3 } from "../api/system";
import PageLoading from "../components/PageLoading";
import PermanentBidPreference from "../components/PermanentBidPreference";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import ConfirmationDialog from "../components/fundamentals/ConfirmationDialog";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import SuccessDialog from "../components/fundamentals/SuccessDialog";
import { selectUserSession } from "../slices/userSession/userSessionSlice";
import { logout } from "../util/authentication";
import {
  BREADCRUMBS_TITLE,
  CANCEL,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  CLIENT,
  HOME_PATH,
  SUBMIT,
  DELETE,
  TWU_CONTRACT,
  API_TIMEOUT,
  API_POLLING_INTERVAL,
} from "../util/constants";
import { PERM_BID_UPDATE_TYPE } from "../util/bid/bidConstants.js";
import {
  CANCEL_TITLE,
  DELETE_PERMANENT_BID_EMPLOYEE_TITLE,
  ERROR_MESSAGE,
  EXPORT_PDF,
  LAST_UPDATED,
  MY_PERMANENT_BID_TWU_TITLE,
  PERMANENT_BID_CANCEL_MESSAGE,
  PERMANENT_BID_DELETE_MESSAGE_EMPLOYEE,
  PERMANENT_BID_SAVE_MESSAGE,
  PERMANENT_BID_SUBMISSION_ERROR_MESSAGE,
  PERMANENT_BID_DELETE_MESSAGE,
  PERMANENT_BID_DELETION_ERROR_MESSAGE,
  SUBMIT_PERMANENT_BID_DIALOG_TITLE,
  SUBMIT_PERMANENT_BID_TITLE,
  SUCCESS_TITLE,
  TWU_PERMANENT_BID_MESSAGE,
  UPDATE_PERMANENT_BID_TWU_ALERT_MESSAGE,
} from "../util/messageConstants";
import { areDuplicatePreferencesExists, validatePreferences } from "../util/permanentBidInputValidation";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../util/timeZoneService";
import { getWorkgroupGivenJobCode } from "../util/workgroupMapper.js";
import isEqual from "lodash.isequal";

const useStyles = makeStyles((theme) => ({
  lastUpdated: {
    textAlign: "left",
    color: theme.palette.secondary.swaGray4,
    marginRight: "3.5vw",
  },
  pageTitleLeft: {
    fontSize: "24px",
    maxWidth: "90vw",
    textAlign: "left",
    marginTop: "10px",
  },
  root: {
    margin: "auto",
    width: "100%",
    marginLeft: "2rem",
    paddingRight: "4rem",
  },
  cardStyle: {
    marginTop: "40px",
    borderRadius: 0,
  },
  mobileCardStyle: {
    marginTop: 0,
    marginBottom: "450px",
  },
  bidScroll: {
    width: "100%",
    height: "90vh",
    overflowY: "scroll",
    position: "absolute",
    textAlign: "left",
  },
  bidDesktopScroll: {
    height: "100%",
    overflowY: "scroll",
    overflowX: "hidden",
    position: "absolute",
    textAlign: "left",
    width: "100%",
    paddingBottom: "100px",
  },
  bidButton: {
    paddingTop: "8px",
  },
  bidButtonMobile: {
    paddingTop: "8px",
    padding: "16px",
  },
  bidAlert: {
    textAlign: "left",
    paddingTop: 0,
    paddingBottom: 0,
    fontSize: "14px",
  },
  bidTitleMobile: {
    padding: "16px",
    paddingBottom: "8px",
  },
  link: {
    display: "none",
  },
}));

const EmployeePermanentBidTwu = (props) => {
  const classes = useStyles();
  // Redux State
  const userSession = useSelector(selectUserSession);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [permanentBid, setPermanentBid] = useState([]);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [permanentBidVersion, setPermanentBidVersion] = useState(null);
  const [lastUpdatedTimestamp, setLastUpdatedTimestamp] = useState("");
  const [lastUpdatedByFirstName, setLastUpdatedByFirstName] = useState("");
  const [lastUpdatedByLastName, setLastUpdatedByLastName] = useState("");
  const [permanentBidInitialValues, setPermanentBidInitialValues] = useState([]);
  const [noPreferencesAlert, setNoPreferencesAlert] = useState(false);
  const employeeBreadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.MY_PERMANENT_BID];
  const breadcrumbsLink = [HOME_PATH];
  const [savePermanentBidLoading, setSavePermanentBidLoading] = useState(false);
  const [isDeleteSuccessDialogOpen, setIsDeleteSuccessDialogOpen] = useState(false);
  const [isDeletePermanentBidLoading, setIsDeletePermanentBidLoading] = useState(false);
  const [isErrorPermanentBidSave, setIsErrorPermanentBidSave] = useState(false);
  const [isErrorPermanentBidDelete, setIsErrorPermanentBidDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidationLoading, setIsValidationLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isPermanentBidEmpty, setIsPermanentBidEmpty] = useState(true);
  const linkRef = useRef();
  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(!userSession.isAdmin);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  const downloadPDF = async () => {
    setExportLoading(true);
    try {
      let res = await fetchPermanentBidPDF(userSession.eid);
      res = await res.json();

      if (!res.error && !res.message) {
        const a = linkRef.current;
        a.href = res.downloadUrl;
        a.click();
        a.href = "";
        setExportLoading(false);
      } else {
        console.error("error while downloading permanent bid pdf!");
      }
    } catch {
      console.error("error while downloading permanent bid pdf!");
    }
  };
  useEffect(() => {
    fetchPermanentBid();
    // eslint-disable-next-line
  }, []);

  const handleNoClickedCancelDialog = () => {
    setIsCancelDialogOpen(false);
  };

  const handleClickCancelDialogOpen = () => {
    setIsCancelDialogOpen(true);
    setIsSuccessDialogOpen(false);
  };

  const handleClickDelete = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleClickNoDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleClickYesDeleteDialog = () => {
    setIsDeletePermanentBidLoading(true);
    deletePermanentBid();
  };

  const handleClickSubmitDialogOpen = () => {
    setIsValidationLoading(true);
    if (checkPreferences()) {
      setIsSuccessDialogOpen(true);
    }
    setIsValidationLoading(false);
  };

  const handleCancelDialogYesButtonClicked = () => {
    setIsCancelDialogOpen(false);
  };

  const handleSubmitDialogYesButtonClicked = () => {
    setSavePermanentBidLoading(true);
    savePermanentBid();
  };

  const isPermanentBidChanged = () => {
    const filteredPreferences = permanentBid.map(({ id, message, ...rest }) => rest);
    const filteredPreferencesInitialValues = permanentBidInitialValues.map(({ id, message, ...rest }) => rest);

    return !isEqual(filteredPreferences, filteredPreferencesInitialValues);
  };

  const setPermanentBidData = (res) => {
    if (
      !res.error &&
      !res.message &&
      res.latestPermanentBidEvent &&
      res.latestPermanentBidEvent.updateType !== "DELETE"
    ) {
      const latestPermanentBid = res.latestPermanentBidEvent;
      const preferences = latestPermanentBid.preferences.map((preference, i) => ({
        id: i + 1,
        daysOff: preference.daysOff,
        position: preference.position,
        startTime: `${preference.startTimeStartRange.slice(0, 2)}:${preference.startTimeStartRange.slice(
          2
        )}-${preference.startTimeEndRange.slice(0, 2)}:${preference.startTimeEndRange.slice(2)}`,
        startTimeStartRange: preference.startTimeStartRange,
        startTimeEndRange: preference.startTimeEndRange,
      }));

      setPermanentBidInitialValues(preferences);
      setPermanentBid(preferences);
      setLastUpdatedByFirstName(latestPermanentBid.modifiedByFirstName);
      setLastUpdatedByLastName(latestPermanentBid.modifiedByLastName);
      setLastUpdatedTimestamp(parseInt(latestPermanentBid.createdAt));
      setIsPermanentBidEmpty(false);
    } else if (res?.latestPermanentBidEvent?.updateType === "DELETE") {
      setLastUpdatedByFirstName(res.latestPermanentBidEvent.modifiedByFirstName);
      setLastUpdatedByLastName(res.latestPermanentBidEvent.modifiedByLastName);
      setLastUpdatedTimestamp(parseInt(res.latestPermanentBidEvent.createdAt));
      setPermanentBid([]);
      setPermanentBidInitialValues([]);
      setIsPermanentBidEmpty(true);
    }
    setPermanentBidVersion(res.error || res.message ? null : res.latestPermanentBidEvent.v);
    setIsLoading(false);
  };

  const fetchPermanentBid = () => {
    setIsLoading(true);
    fetchLatestPermanentBid(userSession.eid)
      .then((result) => result.json())
      .then((res) => {
        setPermanentBidData(res);
      })
      .catch((err) => {
        console.log("error fetching latest permanent bid!", err);
        setIsLoading(false);
      });
  };

  // fetch perm bid till it matches requestId
  const longPollingPermBidFetch = (requestId) => {
    const startTime = DateTime.now();

    function fetchBid() {
      fetchLatestPermanentBid(userSession.eid)
        .then((result) => result.json())
        .then((res) => {
          if (res?.latestPermanentBidEvent?.requestId === requestId) {
            setPermanentBidData(res);
            setIsSubmitDialogOpen(true);
            setSavePermanentBidLoading(false);
            setIsSuccessDialogOpen(false);
          } else if (!res || res.error) {
            // stop polling if one call fails
            setIsSubmitDialogOpen(true);
            setSavePermanentBidLoading(false);
            setIsSuccessDialogOpen(false);
            setIsErrorPermanentBidSave(true);
          } else {
            const currentTime = DateTime.now();
            const elapsedTime = currentTime - startTime;
            if (elapsedTime < API_TIMEOUT) {
              // recursive call till requestId matches or timeout happens
              setTimeout(fetchBid, API_POLLING_INTERVAL);
            } else {
              // stop polling when timeout
              setIsSubmitDialogOpen(true);
              setSavePermanentBidLoading(false);
              setIsSuccessDialogOpen(false);
              setIsErrorPermanentBidSave(true);
            }
          }
        })
        .catch((err) => {
          console.log("error fetching latest permanent bid!", err);
          setIsSubmitDialogOpen(true);
          setSavePermanentBidLoading(false);
          setIsSuccessDialogOpen(false);
          setIsErrorPermanentBidSave(true);
        });
    }

    fetchBid();
  };

  const savePermanentBid = () => {
    if (checkPreferences() && isPermanentBidChanged()) {
      const preferences = permanentBid.map(({ id, startTime, message, ...rest }) => rest);
      const postBody = {
        eid: userSession.eid,
        contract: TWU_CONTRACT,
        createdBy: CLIENT,
        v: permanentBidVersion,
        workgroup: getWorkgroupGivenJobCode(userSession.jobCode),
        flags: { isAdminCreated: false },
        station: userSession.activeStation,
        preferences: preferences,
        updateType: PERM_BID_UPDATE_TYPE.CREATE,
        modifiedByEid: userSession.eid,
        modifiedByFirstName: userSession.firstName,
        modifiedByLastName: userSession.lastName,
      };
      getPresignedUrl("put_object", "data-passthrough", `PERM_BID_${userSession.eid}%2Fperm_bid.json`)
        .then((res) => res.json())
        .then((result) => {
          if (result.presignedUrl) {
            uploadFileToS3(result.presignedUrl, postBody).then((response) => {
              console.log(response);
              if (response.status === 200) {
                console.log("Success!");
                const requestId = response.headers.get("x-amz-request-id");
                longPollingPermBidFetch(requestId);
              } else {
                console.error("Failure in bid S3 upload!");
                setIsSubmitDialogOpen(true);
                setSavePermanentBidLoading(false);
                setIsSuccessDialogOpen(false);
                setIsErrorPermanentBidSave(true);
              }
            });
          } else {
            console.error("Failure in getting S3 presigned url!");
            setIsSubmitDialogOpen(true);
            setSavePermanentBidLoading(false);
            setIsSuccessDialogOpen(false);
            setIsErrorPermanentBidSave(true);
          }
        });
    } else {
      setIsSuccessDialogOpen(false);
      setSavePermanentBidLoading(false);
    }
  };

  const deletePermanentBid = async () => {
    const postBody = {
      eid: userSession.eid,
      createdBy: "CLIENT",
      flags: { isAdminCreated: false },
      updateType: "DELETE",
      modifiedByFirstName: userSession.firstName,
      modifiedByLastName: userSession.lastName,
      modifiedByEid: userSession.eid,
      station: userSession.homeStation,
      contract: TWU_CONTRACT,
      workgroup: getWorkgroupGivenJobCode(userSession.jobCode),
      v: permanentBidVersion,
    };
    await deletePermanentBidEvent(postBody)
      .then((res) => res.json())
      .then(async (response) => {
        if (!response.message) {
          setIsDeleteDialogOpen(false);
          setIsDeletePermanentBidLoading(false);
          setIsErrorPermanentBidDelete(false);
          setIsDeleteSuccessDialogOpen(true);
        } else {
          throw new Error();
        }
      })
      .catch((err) => {
        console.log(err, "error");
        setIsDeleteDialogOpen(false);
        setIsDeletePermanentBidLoading(false);
        setIsErrorPermanentBidDelete(true);
        setIsDeleteSuccessDialogOpen(true);
      });
  };

  const renderCancelAreYouSureDialog = () => {
    return (
      <ConfirmationDialog
        title={CANCEL_TITLE}
        isDialogOpen={isCancelDialogOpen}
        message={PERMANENT_BID_CANCEL_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA)}
        handleNoButtonClicked={handleNoClickedCancelDialog}
        redirectTo={HOME_PATH}
        handleYesButtonClicked={handleCancelDialogYesButtonClicked}
      />
    );
  };

  const renderDeleteAreYouSureDialog = () => {
    return (
      <ConfirmationDialog
        title={DELETE_PERMANENT_BID_EMPLOYEE_TITLE}
        isDialogOpen={isDeleteDialogOpen}
        message={PERMANENT_BID_DELETE_MESSAGE_EMPLOYEE}
        handleNoButtonClicked={handleClickNoDeleteDialog}
        handleYesButtonClicked={handleClickYesDeleteDialog}
        isYesLoading={isDeletePermanentBidLoading}
        yesTitle={DELETE}
        noTitle={CANCEL}
      />
    );
  };

  const renderSubmitAreYouSureDialog = () => {
    return (
      <ConfirmationDialog
        title={SUBMIT_PERMANENT_BID_TITLE}
        isDialogOpen={isSuccessDialogOpen}
        message={SUBMIT_PERMANENT_BID_DIALOG_TITLE}
        handleNoButtonClicked={() => setIsSuccessDialogOpen(false)}
        handleYesButtonClicked={handleSubmitDialogYesButtonClicked}
        isYesLoading={savePermanentBidLoading}
        yesTitle={SUBMIT}
        noTitle={CANCEL}
      />
    );
  };

  const checkPreferences = () => {
    const validatedTableData = validatePreferences(permanentBid);
    if (!permanentBid.length) {
      setNoPreferencesAlert(true);
    } else {
      setNoPreferencesAlert(false);
      setPermanentBid(validatedTableData);
    }
    return !areDuplicatePreferencesExists(validatedTableData) && permanentBid.length;
  };

  const renderPermanentBidPreference = () => (
    <PermanentBidPreference
      permanentBid={permanentBid}
      setPermanentBid={setPermanentBid}
      isDesktopView={props.isDesktopView}
      noPreferencesAlert={noPreferencesAlert}
      setNoPreferencesAlert={setNoPreferencesAlert}
      twuContract
    />
  );

  const renderTwuPermanentBidEmployeePage = () => {
    return (
      <div className={props.isDesktopView ? classes.bidDesktopScroll : classes.bidScroll}>
        <div>
          {props.isDesktopView && (
            <StarsBreadcrumbs
              isConfirmationRequired={isPermanentBidChanged()}
              breadcrumbsLinks={breadcrumbsLink}
              breadcrumbsList={employeeBreadcrumbsList}
              cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA}
            />
          )}
        </div>
        <div className={props.isDesktopView ? classes.root : undefined}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid
              item
              container
              justifyContent="flex-start"
              sm={12}
              xs={12}
              md={8}
              lg={8}
              className={props.isDesktopView ? undefined : classes.bidTitleMobile}
            >
              <Grid container className={classes.pageTitleLeft}>
                {MY_PERMANENT_BID_TWU_TITLE}
              </Grid>
              <br />
              <Grid className={classes.lastUpdated}>
                {!isLoading &&
                lastUpdatedTimestamp !== "" &&
                lastUpdatedByFirstName !== "" &&
                lastUpdatedByLastName !== ""
                  ? LAST_UPDATED +
                    lastUpdatedByLastName +
                    ", " +
                    lastUpdatedByFirstName +
                    " " +
                    getStationTimeZoneDateTimeStringFromUTCInteger(
                      lastUpdatedTimestamp,
                      userSession.homeStation
                    ).toFormat("MM/dd/yyyy T ZZZZ")
                  : ""}
              </Grid>
            </Grid>
            <Grid item sm={12} xs={12} md={4} lg={4}>
              <Grid
                container
                justifyContent="flex-end"
                spacing={2}
                className={props.isDesktopView ? classes.bidButton : classes.bidButtonMobile}
              >
                <Grid item>
                  <Button
                    sx={{ width: "100px" }}
                    variant="outlined"
                    color="primary"
                    onClick={handleClickCancelDialogOpen}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    sx={{ width: "100px" }}
                    variant="outlined"
                    color="error"
                    onClick={handleClickDelete}
                    disabled={isPermanentBidEmpty}
                  >
                    Delete
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    sx={{ width: "100px" }}
                    variant="contained"
                    color="primary"
                    onClick={handleClickSubmitDialogOpen}
                    disabled={isValidationLoading || !isPermanentBidChanged()}
                  >
                    {isValidationLoading ? <CircularProgress size={25} /> : "Submit"}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Card className={props.isDesktopView ? classes.cardStyle : classes.mobileCardStyle} variant="outlined">
            {props.isDesktopView && (
              <CardHeader
                titleTypographyProps={{
                  fontSize: "20px",
                  fontWeight: "700px",
                }}
                action={
                  <>
                    <Button
                      variant="outlined"
                      startIcon={!exportLoading ? <UploadFileIcon /> : <CircularProgress size={20} />}
                      disabled={isPermanentBidEmpty}
                      onClick={downloadPDF}
                    >
                      {EXPORT_PDF}
                    </Button>
                    <a className={classes.link} ref={linkRef} href={"/"}>
                      Download Report
                    </a>
                  </>
                }
                title={TWU_PERMANENT_BID_MESSAGE}
              />
            )}
            <Divider />
            <CardContent style={{ padding: 0 }}>
              <Alert severity="info" className={classes.bidAlert}>
                {UPDATE_PERMANENT_BID_TWU_ALERT_MESSAGE}
              </Alert>
              <Divider />
              <Grid>{renderPermanentBidPreference(props)}</Grid>{" "}
            </CardContent>
          </Card>
          {renderCancelAreYouSureDialog()}
          {renderDeleteAreYouSureDialog()}
          {renderSubmitAreYouSureDialog()}
          {isSubmitDialogOpen ? (
            <SuccessDialog
              isSuccessDialogOpen={isSubmitDialogOpen}
              title={isErrorPermanentBidSave ? ERROR_MESSAGE : SUCCESS_TITLE}
              message={PERMANENT_BID_SAVE_MESSAGE}
              hasError={isErrorPermanentBidSave}
              error={PERMANENT_BID_SUBMISSION_ERROR_MESSAGE}
              handleOKClick={() => {
                setIsSubmitDialogOpen(false);
                setIsErrorPermanentBidSave(false);
                fetchPermanentBid();
              }}
            />
          ) : (
            ""
          )}
          {isDeleteSuccessDialogOpen && (
            <SuccessDialog
              isSuccessDialogOpen={isDeleteSuccessDialogOpen}
              title={isErrorPermanentBidDelete ? ERROR_MESSAGE : SUCCESS_TITLE}
              message={PERMANENT_BID_DELETE_MESSAGE}
              hasError={isErrorPermanentBidDelete}
              error={PERMANENT_BID_DELETION_ERROR_MESSAGE}
              handleOKClick={() => {
                setIsDeleteSuccessDialogOpen(false);
                fetchPermanentBid();
                setIsErrorPermanentBidDelete(false);
              }}
            />
          )}
        </div>
      </div>
    );
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div data-testid="twu-permanent-bid-page">
      {isLoading || isUserAuthorizedStateLoading ? <PageLoading /> : <>{renderTwuPermanentBidEmployeePage()}</>}
    </div>
  );
};

export default EmployeePermanentBidTwu;
